/* QuickFlip — interaction layer
   Progressive enhancement only: if any of this fails, content stays visible. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----------------------------------------------------------------------
     Scroll reveal
     ---------------------------------------------------------------------- */
  function initReveal(root) {
    var targets = (root || document).querySelectorAll('.qf-reveal:not(.is-in)');
    if (!targets.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px 8% 0px', threshold: 0 }
    );

    targets.forEach(function (el) { io.observe(el); });
  }

  /* ----------------------------------------------------------------------
     Stroke-draw setup: measure each path so the dash animation is exact
     ---------------------------------------------------------------------- */
  function initDraw(root) {
    (root || document).querySelectorAll('.qf-tube--draw').forEach(function (path) {
      try {
        var len = path.getTotalLength();
        if (len) path.style.setProperty('--qf-len', len);
      } catch (e) {
        /* getTotalLength can throw on detached nodes; the CSS fallback covers it */
      }
    });
  }

  /* ----------------------------------------------------------------------
     Count-up stats
     ---------------------------------------------------------------------- */
  function initCounters(root) {
    var nums = (root || document).querySelectorAll('[data-qf-count]');
    if (!nums.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      nums.forEach(function (el) {
        el.textContent = formatNum(parseFloat(el.dataset.qfCount), el.dataset.qfSuffix);
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          countUp(entry.target);
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.4 }
    );

    nums.forEach(function (el) { io.observe(el); });
  }

  function formatNum(n, suffix) {
    return Math.round(n).toLocaleString() + (suffix || '');
  }

  function countUp(el) {
    var target = parseFloat(el.dataset.qfCount);
    var suffix = el.dataset.qfSuffix || '';
    if (isNaN(target)) return;

    var duration = 1600;
    var start = null;

    function frame(ts) {
      if (start === null) start = ts;
      var p = Math.min((ts - start) / duration, 1);
      // easeOutExpo
      var eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
      el.textContent = formatNum(target * eased, suffix);
      if (p < 1) requestAnimationFrame(frame);
    }

    requestAnimationFrame(frame);
  }

  /* ----------------------------------------------------------------------
     Bend selector — morphs the SVG tube between shapes
     ---------------------------------------------------------------------- */
  function initBend(root) {
    (root || document).querySelectorAll('[data-qf-bend]').forEach(function (widget) {
      if (widget.dataset.qfBendReady === 'true') return;
      widget.dataset.qfBendReady = 'true';

      var modes = Array.prototype.slice.call(widget.querySelectorAll('.qf-mode'));
      var paths = Array.prototype.slice.call(widget.querySelectorAll('.qf-bend__path'));
      if (!modes.length || !paths.length) return;

      var timer = null;

      function select(index, userInitiated) {
        modes.forEach(function (m, i) {
          m.setAttribute('aria-selected', i === index ? 'true' : 'false');
          m.tabIndex = i === index ? 0 : -1;
        });

        var d = modes[index].dataset.qfPath;
        if (d) paths.forEach(function (p) { p.setAttribute('d', d); });

        if (userInitiated && timer) {
          clearInterval(timer);
          timer = null;
        }
      }

      modes.forEach(function (mode, i) {
        mode.addEventListener('click', function () { select(i, true); });
        mode.addEventListener('keydown', function (e) {
          var next = null;
          if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = (i + 1) % modes.length;
          if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = (i - 1 + modes.length) % modes.length;
          if (next === null) return;
          e.preventDefault();
          select(next, true);
          modes[next].focus();
        });
      });

      select(0, false);

      // Gentle auto-rotation until the visitor takes over.
      if (!reduceMotion) {
        var idx = 0;
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && !timer) {
              timer = setInterval(function () {
                idx = (idx + 1) % modes.length;
                select(idx, false);
              }, 3200);
            } else if (!entry.isIntersecting && timer) {
              clearInterval(timer);
              timer = null;
            }
          });
        }, { threshold: 0.35 });
        io.observe(widget);
      }
    });
  }

  /* ----------------------------------------------------------------------
     Annotated close-up — pairs each hotspot with its list entry
     ---------------------------------------------------------------------- */
  function initDetail(root) {
    (root || document).querySelectorAll('[data-qf-detail]').forEach(function (widget) {
      if (widget.dataset.qfDetailReady === 'true') return;
      widget.dataset.qfDetailReady = 'true';

      var spots = Array.prototype.slice.call(widget.querySelectorAll('[data-qf-hotspot]'));
      var items = Array.prototype.slice.call(widget.querySelectorAll('[data-qf-item]'));
      if (!spots.length) return;

      function setActive(index, on) {
        var spot = spots[index];
        var item = items[index];
        if (spot) spot.classList.toggle('is-active', on);
        if (item) item.classList.toggle('is-active', on);
      }

      function bind(el, index) {
        if (!el) return;
        el.addEventListener('mouseenter', function () { setActive(index, true); });
        el.addEventListener('mouseleave', function () { setActive(index, false); });
        el.addEventListener('focus', function () { setActive(index, true); });
        el.addEventListener('blur', function () { setActive(index, false); });
      }

      spots.forEach(function (spot, i) {
        bind(spot, i);
        // Tapping a hotspot scrolls its description into view on small screens.
        spot.addEventListener('click', function () {
          setActive(i, true);
          if (items[i] && window.matchMedia('(max-width: 900px)').matches) {
            items[i].scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
          }
        });
      });

      items.forEach(function (item, i) {
        bind(item, i);
        item.tabIndex = 0;
      });
    });
  }

  /* ----------------------------------------------------------------------
     Quantity stepper
     ---------------------------------------------------------------------- */
  function initQty(root) {
    (root || document).querySelectorAll('[data-qf-qty]').forEach(function (widget) {
      if (widget.dataset.qfQtyReady === 'true') return;
      widget.dataset.qfQtyReady = 'true';

      var input = widget.querySelector('.qf-qty__input');
      if (!input) return;

      widget.querySelectorAll('[data-qf-step]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var step = parseInt(btn.dataset.qfStep, 10) || 0;
          var min = parseInt(input.min, 10) || 1;
          var next = (parseInt(input.value, 10) || min) + step;
          input.value = next < min ? min : next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      // Guard against someone typing 0 or a negative straight into the field.
      input.addEventListener('change', function () {
        var min = parseInt(input.min, 10) || 1;
        if (!input.value || parseInt(input.value, 10) < min) input.value = min;
      });
    });
  }

  /* ----------------------------------------------------------------------
     FAQ accordion
     ---------------------------------------------------------------------- */
  function initFaq(root) {
    (root || document).querySelectorAll('[data-qf-faq]').forEach(function (item) {
      if (item.dataset.qfFaqReady === 'true') return;
      item.dataset.qfFaqReady = 'true';

      var btn = item.querySelector('.qf-faq__q');
      var panel = item.querySelector('.qf-faq__a');
      if (!btn || !panel) return;

      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        panel.hidden = open;
      });
    });
  }

  /* ----------------------------------------------------------------------
     Product gallery — thumbnails swap the main image
     ---------------------------------------------------------------------- */
  function initGallery(root) {
    (root || document).querySelectorAll('[data-qf-gallery]').forEach(function (gallery) {
      if (gallery.dataset.qfGalleryReady === 'true') return;
      gallery.dataset.qfGalleryReady = 'true';

      var main = gallery.querySelector('.qf-pdp__img');
      var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-qf-thumb]'));
      if (!main || !thumbs.length) return;

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var src = thumb.dataset.qfThumb;
          if (!src) return;
          // Drop the responsive attributes so the chosen source actually wins.
          main.removeAttribute('srcset');
          main.removeAttribute('sizes');
          main.src = src;
          thumbs.forEach(function (t) { t.classList.toggle('is-active', t === thumb); });
        });
      });
    });
  }

  /* ----------------------------------------------------------------------
     Before/after slider
     ---------------------------------------------------------------------- */
  function initCompare(root) {
    (root || document).querySelectorAll('[data-qf-compare]').forEach(function (frame) {
      if (frame.dataset.qfCompareReady === 'true') return;
      frame.dataset.qfCompareReady = 'true';

      var range = frame.querySelector('[data-qf-compare-range]');
      var clip = frame.querySelector('[data-qf-compare-clip]');
      var handle = frame.querySelector('[data-qf-compare-handle]');
      if (!range || !clip) return;

      function apply() {
        var v = range.value;
        clip.style.width = v + '%';
        if (handle) handle.style.left = v + '%';
      }

      range.addEventListener('input', apply);
      apply();
    });
  }

  /* ----------------------------------------------------------------------
     Buy now — add the item, then go straight to checkout
     ---------------------------------------------------------------------- */
  function initBuyNow(root) {
    (root || document).querySelectorAll('[data-qf-buynow]').forEach(function (btn) {
      if (btn.dataset.qfBuynowReady === 'true') return;
      btn.dataset.qfBuynowReady = 'true';

      btn.addEventListener('click', function () {
        var form = btn.closest('form');
        if (!form) return;

        btn.setAttribute('aria-busy', 'true');

        fetch('/cart/add.js', {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'application/json' }
        })
          .then(function (res) {
            if (!res.ok) throw new Error('add failed');
            window.location.href = '/checkout';
          })
          .catch(function () {
            // Fall back to the normal add-to-cart path rather than dead-ending.
            btn.removeAttribute('aria-busy');
            form.submit();
          });
      });
    });
  }

  /* ---------------------------------------------------------------------- */
  function initAll(root) {
    initBuyNow(root);
    initQty(root);
    initFaq(root);
    initGallery(root);
    initCompare(root);
    initReveal(root);
    initDraw(root);
    initCounters(root);
    initBend(root);
    initDetail(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Re-run when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
